/* eslint-disable max-len */
import {
  catchError, debounceTime, filter, from, map, mergeMap, Observable, of, takeUntil,
} from 'rxjs';
import { AxiosError } from 'axios';
import Config from '../../../../Common/Config';
import {
  makeGetRequest, makePostRequest,
} from '../../../../Common/NetworkOps';
import {
  AddFlaggedKitsApiResponse, AddFlaggedKitsData, DeleteFlaggedKitsResponse, GetAllFlaggedKitsApiResponse,
  PayloadTypeDeleteFlaggedKits, PayloadTypeGetFlaggedKits, UpdateFlaggedKitsApiResponse, UpdateFlaggedKitsData,
} from '../utils/types';
import { showErrorToaster, showSuccessToaster } from '../../../../Common/ComponentToast/ComponentSuccessToasts';
import {
  addFlaggedKitsStart, deleteFlaggedKitsStart, failureAddFlaggedKits, failureDeleteFlaggedKits, failureFlaggedKits,
  failureUpdateFlaggedKits, getFlaggedKitsStart,
  FlaggedKitsTypeActions, successAddFlaggedKits, successDeleteFlaggedKits, successFlaggedKits, successUpdateFlaggedKits, updateFlaggedKits,
} from './sliceFlaggedKits';

async function GetFlaggedKits(data: PayloadTypeGetFlaggedKits): Promise<GetAllFlaggedKitsApiResponse> {
  const url = `${Config.Lists.getFlaggedKits}?pageNumber=${data.page}&pageSize=${data.rowsPerPage}&searchItem=${data.searchQuery}&sortColumn=${data.sortCol}&sortDirection=${data.sortDir}`;
  const result = await makeGetRequest<GetAllFlaggedKitsApiResponse>(url);
  return result?.data;
}

export const epicGetFlaggedKits = (action$: Observable<FlaggedKitsTypeActions>) => action$.pipe(
  filter(getFlaggedKitsStart.match),
  debounceTime(250),
  map((x) => x.payload),
  mergeMap((data: PayloadTypeGetFlaggedKits) => from(GetFlaggedKits(data)).pipe(
    map((res: GetAllFlaggedKitsApiResponse) => {
      if (res.BMT.ResponseCode === Config.SUCCESS_CODE) {
        return successFlaggedKits(res?.BMT?.Result);
      }
      return failureFlaggedKits(res?.BMT?.ResponseMessage);
    }),
    takeUntil(action$.pipe(filter(getFlaggedKitsStart.match))),
    catchError((error: AxiosError<GetAllFlaggedKitsApiResponse>) => of(failureFlaggedKits(error?.response?.data?.BMT?.ResponseMessage as string))),
  )),
);

async function UpdateFlaggedKits(data: UpdateFlaggedKitsData): Promise<UpdateFlaggedKitsApiResponse> {
  const url = `${Config.Lists.updateFlaggedKits}`;
  const result = await makePostRequest<UpdateFlaggedKitsApiResponse>(url, data);
  return result?.data;
}

export const epicUpdateFlaggedKits = (action$: Observable<FlaggedKitsTypeActions>) => action$.pipe(
  filter(updateFlaggedKits.match),
  debounceTime(250),
  map((x) => x.payload),
  mergeMap((data) => from(UpdateFlaggedKits(data)).pipe(
    mergeMap((res: UpdateFlaggedKitsApiResponse) => {
      if (res.BMT.ResponseCode === Config.SUCCESS_CODE) {
        showSuccessToaster(res?.BMT?.ResponseMessage);
        return of(successUpdateFlaggedKits());
      }
      showErrorToaster(res?.BMT?.ResponseMessage);
      return of(failureUpdateFlaggedKits(res?.BMT?.ResponseMessage));
    }),
    takeUntil(action$.pipe(filter(updateFlaggedKits.match))),
    catchError((error: AxiosError<UpdateFlaggedKitsApiResponse>) => {
      const errorMessage = error?.response?.data?.BMT?.ResponseMessage as string;
      of(showErrorToaster(errorMessage));
      return of(failureUpdateFlaggedKits(errorMessage));
    }),
  )),
);

async function AddFlaggedKits(data: AddFlaggedKitsData): Promise<AddFlaggedKitsApiResponse> {
  const url = `${Config.Lists.addFlaggedKits}`;
  const result = await makePostRequest<AddFlaggedKitsApiResponse>(url, data);
  return result?.data;
}

export const epicAddFlaggedKits = (action$: Observable<FlaggedKitsTypeActions>) => action$.pipe(
  filter(addFlaggedKitsStart.match),
  debounceTime(250),
  map((x) => x.payload),
  mergeMap((data) => from(AddFlaggedKits(data)).pipe(
    mergeMap((res: AddFlaggedKitsApiResponse) => {
      if (res.BMT.ResponseCode === Config.POST_SUCCESS_CODE) {
        showSuccessToaster(res?.BMT?.ResponseMessage);
        return of(successAddFlaggedKits());
      }
      showErrorToaster(res?.BMT?.ResponseMessage);
      return of(failureAddFlaggedKits(res?.BMT?.ResponseMessage));
    }),
    takeUntil(action$.pipe(filter(addFlaggedKitsStart.match))),
    catchError((error: AxiosError<AddFlaggedKitsApiResponse>) => {
      const errorMessage = error?.response?.data?.BMT?.ResponseMessage as string;
      of(showErrorToaster(errorMessage));
      return of(failureAddFlaggedKits(errorMessage));
    }),
  )),
);

async function DeleteFlaggedKits(data: PayloadTypeDeleteFlaggedKits): Promise<DeleteFlaggedKitsResponse> {
  const url = `${Config.Lists.deleteFlaggedKits}`;
  const result = await makePostRequest<DeleteFlaggedKitsResponse>(url, data?.pay);
  return result?.data;
}

export const epicDeleteFlaggedKits = (action$: Observable<FlaggedKitsTypeActions>) => action$.pipe(
  filter(deleteFlaggedKitsStart.match),
  map((x) => x.payload),
  mergeMap((data: PayloadTypeDeleteFlaggedKits) => from(DeleteFlaggedKits(data)).pipe(
    mergeMap((res: DeleteFlaggedKitsResponse) => {
      const payload = {
        rowsPerPage: data?.rowsPerPage,
        page: data?.page,
        searchQuery: data?.searchQuery,
        sortCol: data?.sortCol,
        sortDir: data?.sortDir,
      };
      if (res.BMT.ResponseCode === Config.SUCCESS_CODE) {
        showSuccessToaster(res?.BMT?.ResponseMessage);
        return of(successDeleteFlaggedKits(), getFlaggedKitsStart(payload));
      }
      showErrorToaster(res?.BMT?.ResponseMessage);
      return of(failureDeleteFlaggedKits(res?.BMT?.ResponseMessage));
    }),
    takeUntil(action$.pipe(filter(deleteFlaggedKitsStart.match))),
    catchError((error: AxiosError<DeleteFlaggedKitsResponse>) => of(failureDeleteFlaggedKits(error?.response?.data?.BMT?.ResponseMessage as string))),
  )),
);
